/**
 * Admin Storage Service
 *
 * Secure storage service for admin authentication data using Web Crypto API
 * for password hashing and @capacitor/preferences for secure storage.
 *
 * Security Features:
 * - Password hashing with salt using PBKDF2 algorithm
 * - Device-specific storage (not synced with server)
 * - Session management with expiration
 * - Failed attempt tracking with lockout
 */

import { Preferences } from '@capacitor/preferences'
import { AdminAccessLevel } from '@/types/admin'

// Storage keys
const ADMIN_PASSWORD_KEY = 'admin_password_hash'
const ADMIN_SALT_KEY = 'admin_password_salt'
const ADMIN_GOD_PASSWORD_KEY = 'admin_god_password_hash'
const ADMIN_GOD_SALT_KEY = 'admin_god_password_salt'
const ADMIN_SESSION_KEY = 'admin_session'
const ADMIN_FAILED_ATTEMPTS_KEY = 'admin_failed_attempts'

// Constants
const SESSION_DURATION_MS = 30 * 60 * 1000 // 30 minutes
const MAX_FAILED_ATTEMPTS = 3
const LOCKOUT_DURATION_MS = 5 * 60 * 1000 // 5 minutes
const PBKDF2_ITERATIONS = 100000 // Industry standard for PBKDF2

/**
 * Admin session data
 */
export interface AdminSession {
    isAuthenticated: boolean
    expiryTime: number
    accessLevel: AdminAccessLevel
}

/**
 * Failed attempt tracking
 */
export interface FailedAttemptData {
    count: number
    lastAttemptTime: number
    lockoutUntil: number | null
}

/**
 * Generates a random salt for password hashing
 */
async function generateSalt(): Promise<Uint8Array> {
    return crypto.getRandomValues(new Uint8Array(16))
}

/**
 * Converts ArrayBuffer to hex string
 */
function arrayBufferToHex(buffer: ArrayBuffer | Uint8Array): string {
    return Array.from(new Uint8Array(buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')
}

/**
 * Converts hex string to Uint8Array
 */
function hexToUint8Array(hex: string): Uint8Array {
    const matches = hex.match(/.{1,2}/g)
    if (!matches) {
        throw new Error('Invalid hex string')
    }
    return new Uint8Array(matches.map(byte => parseInt(byte, 16)))
}

/**
 * Hashes a password using PBKDF2 algorithm with the provided salt
 */
async function hashPassword(password: string, salt: Uint8Array): Promise<string> {
    const encoder = new TextEncoder()
    const passwordBuffer = encoder.encode(password)

    // Import password as a key
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        passwordBuffer,
        { name: 'PBKDF2' },
        false,
        ['deriveBits']
    )

    // Ensure salt is a proper ArrayBuffer-backed Uint8Array
    const saltBuffer = new Uint8Array(salt)

    // Derive bits using PBKDF2
    const derivedBits = await crypto.subtle.deriveBits(
        {
            name: 'PBKDF2',
            salt: saltBuffer,
            iterations: PBKDF2_ITERATIONS,
            hash: 'SHA-256'
        },
        keyMaterial,
        256 // 256 bits = 32 bytes
    )

    return arrayBufferToHex(derivedBits)
}

/**
 * Sets the admin password (first-time setup or password change)
 * @param password - The new password to set (must be at least 6 characters)
 * @throws Error if password is less than 6 characters
 */
export async function setAdminPassword(password: string): Promise<void> {
    if (password.length < 6) {
        throw new Error('Password must be at least 6 characters')
    }

    const salt = await generateSalt()
    const hash = await hashPassword(password, salt)

    await Preferences.set({
        key: ADMIN_PASSWORD_KEY,
        value: hash
    })

    await Preferences.set({
        key: ADMIN_SALT_KEY,
        value: arrayBufferToHex(salt)
    })
}

/**
 * Sets the god mode admin password
 * @param password - The new god mode password to set (must be at least 6 characters)
 * @throws Error if password is less than 6 characters
 */
export async function setAdminGodPassword(password: string): Promise<void> {
    if (password.length < 6) {
        throw new Error('Password must be at least 6 characters')
    }

    const salt = await generateSalt()
    const hash = await hashPassword(password, salt)

    await Preferences.set({
        key: ADMIN_GOD_PASSWORD_KEY,
        value: hash
    })

    await Preferences.set({
        key: ADMIN_GOD_SALT_KEY,
        value: arrayBufferToHex(salt)
    })
}

/**
 * Validates the provided password against the stored hash
 * @param password - The password to validate
 * @returns true if password is correct, false otherwise
 */
export async function validateAdminPassword(password: string): Promise<boolean> {
    const { value: storedHash } = await Preferences.get({ key: ADMIN_PASSWORD_KEY })
    const { value: storedSaltHex } = await Preferences.get({ key: ADMIN_SALT_KEY })

    if (!storedHash || !storedSaltHex) {
        return false
    }

    const salt = hexToUint8Array(storedSaltHex)
    const hash = await hashPassword(password, salt)

    return hash === storedHash
}

/**
 * Validates the provided password against the stored god mode hash
 * @param password - The password to validate
 * @returns true if password is correct, false otherwise
 */
export async function validateAdminGodPassword(password: string): Promise<boolean> {
    const { value: storedHash } = await Preferences.get({ key: ADMIN_GOD_PASSWORD_KEY })
    const { value: storedSaltHex } = await Preferences.get({ key: ADMIN_GOD_SALT_KEY })

    if (!storedHash || !storedSaltHex) {
        return false
    }

    const salt = hexToUint8Array(storedSaltHex)
    const hash = await hashPassword(password, salt)

    return hash === storedHash
}

/**
 * Checks if admin password has been set up
 * @returns true if password exists, false otherwise
 */
export async function hasAdminPassword(): Promise<boolean> {
    const { value } = await Preferences.get({ key: ADMIN_PASSWORD_KEY })
    return !!value
}

/**
 * Checks if god mode password has been set up
 * @returns true if god mode password exists, false otherwise
 */
export async function hasAdminGodPassword(): Promise<boolean> {
    const { value } = await Preferences.get({ key: ADMIN_GOD_PASSWORD_KEY })
    return !!value
}

/**
 * Creates a new admin session with expiration time
 * @param accessLevel - The access level for this session
 */
export async function createAdminSession(accessLevel: AdminAccessLevel = AdminAccessLevel.NORMAL): Promise<void> {
    const session: AdminSession = {
        isAuthenticated: true,
        expiryTime: Date.now() + SESSION_DURATION_MS,
        accessLevel
    }

    await Preferences.set({
        key: ADMIN_SESSION_KEY,
        value: JSON.stringify(session)
    })
}

/**
 * Gets the current admin session
 * @returns AdminSession or null if no valid session exists
 */
export async function getAdminSession(): Promise<AdminSession | null> {
    const { value } = await Preferences.get({ key: ADMIN_SESSION_KEY })

    if (!value) {
        return null
    }

    try {
        const session = JSON.parse(value) as AdminSession

        // Check if session has expired
        if (session.expiryTime < Date.now()) {
            await clearAdminSession()
            return null
        }

        return session
    } catch (error) {
        console.error('Failed to parse admin session', error)
        return null
    }
}

/**
 * Clears the current admin session
 */
export async function clearAdminSession(): Promise<void> {
    await Preferences.remove({ key: ADMIN_SESSION_KEY })
}

/**
 * Extends the current session expiration time
 */
export async function extendAdminSession(): Promise<void> {
    const session = await getAdminSession()

    if (session) {
        session.expiryTime = Date.now() + SESSION_DURATION_MS
        await Preferences.set({
            key: ADMIN_SESSION_KEY,
            value: JSON.stringify(session)
        })
    }
}

/**
 * Gets failed attempt data
 */
export async function getFailedAttempts(): Promise<FailedAttemptData> {
    const { value } = await Preferences.get({ key: ADMIN_FAILED_ATTEMPTS_KEY })

    if (!value) {
        return {
            count: 0,
            lastAttemptTime: 0,
            lockoutUntil: null
        }
    }

    try {
        return JSON.parse(value) as FailedAttemptData
    } catch (error) {
        console.error('Failed to parse failed attempts data', error)
        return {
            count: 0,
            lastAttemptTime: 0,
            lockoutUntil: null
        }
    }
}

/**
 * Records a failed login attempt
 * @returns Updated failed attempt data
 */
export async function recordFailedAttempt(): Promise<FailedAttemptData> {
    const attemptData = await getFailedAttempts()

    attemptData.count++
    attemptData.lastAttemptTime = Date.now()

    // Apply lockout after max attempts
    if (attemptData.count >= MAX_FAILED_ATTEMPTS) {
        attemptData.lockoutUntil = Date.now() + LOCKOUT_DURATION_MS
    }

    await Preferences.set({
        key: ADMIN_FAILED_ATTEMPTS_KEY,
        value: JSON.stringify(attemptData)
    })

    return attemptData
}

/**
 * Clears failed attempt counter (on successful login)
 */
export async function clearFailedAttempts(): Promise<void> {
    await Preferences.remove({ key: ADMIN_FAILED_ATTEMPTS_KEY })
}

/**
 * Checks if admin is currently locked out
 * @returns true if locked out, false otherwise
 */
export async function isLockedOut(): Promise<boolean> {
    const attemptData = await getFailedAttempts()

    if (!attemptData.lockoutUntil) {
        return false
    }

    // Check if lockout period has passed
    if (attemptData.lockoutUntil < Date.now()) {
        await clearFailedAttempts()
        return false
    }

    return true
}

/**
 * Gets remaining lockout time in milliseconds
 * @returns milliseconds remaining, or 0 if not locked out
 */
export async function getRemainingLockoutTime(): Promise<number> {
    const attemptData = await getFailedAttempts()

    if (!attemptData.lockoutUntil) {
        return 0
    }

    const remaining = attemptData.lockoutUntil - Date.now()
    return remaining > 0 ? remaining : 0
}

/**
 * Clears all admin data (for "Forgot Password" flow)
 * WARNING: This will delete all admin settings and require re-setup
 */
export async function clearAllAdminData(): Promise<void> {
    await Preferences.remove({ key: ADMIN_PASSWORD_KEY })
    await Preferences.remove({ key: ADMIN_SALT_KEY })
    await Preferences.remove({ key: ADMIN_GOD_PASSWORD_KEY })
    await Preferences.remove({ key: ADMIN_GOD_SALT_KEY })
    await Preferences.remove({ key: ADMIN_SESSION_KEY })
    await Preferences.remove({ key: ADMIN_FAILED_ATTEMPTS_KEY })
}
